import useSWR, { type SWRConfiguration } from 'swr';
import denApi from '@/lib/api/denApi';
import { useAuthClient } from '@/lib/authClient';

type DeviceStatus = {
  connected: boolean;
  recoverySetUp: boolean;
};

export function useDeviceConnection(options?: SWRConfiguration) {
  const session = useAuthClient().session;

  const fetcher = async (): Promise<DeviceStatus> =>
    await denApi.get<DeviceStatus, void>('link-device/status');

  const { data, isLoading, isValidating, error, mutate } = useSWR<DeviceStatus>(
    session.data ? 'link-device/status' : null,
    fetcher,
    { revalidateOnFocus: false, ...(options ?? {}) }
  );

  // Treat stale-while-revalidate as "still checking" when there's no fresh data yet.
  // isLoading is only true when SWR has no cached data at all.
  // isValidating is true during background revalidation of stale cached data.
  // Without this, SWR immediately returns stale data from another component's fetch
  // (e.g. RequireFullAuthWithMobile guard), causing a flash of incorrect content.
  const isChecking = session.isPending || isLoading || (!data && isValidating);
  const isConnected = !!data?.connected && !error;
  const isRecoverySetUp = !!data?.recoverySetUp && isConnected;

  return {
    isChecking,
    isConnected,
    isRecoverySetUp,
    status: data,
    error,
    refetch: mutate,
  };
}
